import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, AlertCircle, CheckCircle } from "lucide-react";
import api from "../services/api";

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    username: "",
    student_id: "",
    password: "",
    class_group: "",
    level: ""
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const getSafeRedirect = () => {
    const redirectRaw = searchParams.get("redirect") || "/";
    if (!redirectRaw.startsWith("/")) return "/";
    return redirectRaw;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = "กรุณากรอกชื่อผู้ใช้";
    if (!formData.password) newErrors.password = "กรุณากรอกรหัสผ่าน";
    else if (formData.password.length < 4)
      newErrors.password = "รหัสผ่านต้องมีอย่างน้อย 4 ตัวอักษร";
    if (formData.level && isNaN(Number(formData.level))) newErrors.level = "ชั้นปีต้องเป็นตัวเลข";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const res = await api.post("/signup", formData);
      const data = res.data;
      setIsLoading(false);

      if (data.success) {
        setSignupSuccess(true);
        localStorage.setItem("role", data.role || "user");
        if (data.token) localStorage.setItem("token", data.token);
        if (data.userId) localStorage.setItem("userId", data.userId);

        const redirect = getSafeRedirect();
        setTimeout(() => {
          if (redirect.startsWith("/upload") && (data.role || "").toLowerCase() !== "student") {
            navigate("/", {
              state: { message: "สมัครสำเร็จ แต่บัญชีนี้ไม่มีสิทธิ์เข้าใช้งานหน้าอัปโหลด" },
            });
            return;
          }
          navigate(redirect);
        }, 1200);
      } else {
        setErrors({ username: data.message || "สมัครไม่สำเร็จ" });
      }
    } catch (err) {
      console.error("Signup error:", err);
      setIsLoading(false);
      setErrors({ username: "เกิดข้อผิดพลาด กรุณาลองใหม่" });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-brand-50 to-brand-100">
      <div className="bg-white/60 backdrop-blur p-6 rounded-3xl shadow-2xl w-full max-w-md border border-brand-100">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-500 to-brand-600 rounded-full mb-4">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">สมัครสมาชิก</h2>
          <p className="text-gray-600">กรอกข้อมูลเพื่อสร้างบัญชีใหม่</p>
        </div>

        {signupSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">
              สมัครสมาชิกสำเร็จ! กำลังพาไปยังหน้าที่คุณต้องการ...
            </span>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-6 bg-brand-50/60 p-6 rounded-xl shadow-inner border border-brand-200"
        >
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อผู้ใช้</label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleInputChange("username", e.target.value)}
              className={`block w-full px-3 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                errors.username ? "border-red-300 focus:ring-red-500" : "border-brand-200"
              }`}
              placeholder="กรอกชื่อผู้ใช้"
            />
            {errors.username && (
              <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle className="w-4 h-4" /> <span>{errors.username}</span>
              </p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">รหัสผ่าน</label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              className={`block w-full px-3 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                errors.password ? "border-red-300 focus:ring-red-500" : "border-brand-200"
              }`}
              placeholder="กรอกรหัสผ่าน"
            />
            {errors.password && (
              <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle className="w-4 h-4" /> <span>{errors.password}</span>
              </p>
            )}
          </div>

          {/* Student ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">รหัสนักศึกษา</label>
            <input
              type="text"
              value={formData.student_id}
              onChange={(e) => handleInputChange("student_id", e.target.value)}
              className="block w-full px-3 py-3 border rounded-xl shadow-sm border-brand-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="รหัสนักศึกษา เช่น 367409221000"
            />
          </div>

          {/* Class Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">กลุ่มชั้นเรียน</label>
            <input
              type="text"
              value={formData.class_group}
              onChange={(e) => handleInputChange("class_group", e.target.value)}
              className="block w-full px-3 py-3 border rounded-xl shadow-sm border-brand-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="กลุ่มชั้นเรียน เช่น ITS36721N , ITS36741N"
            />
          </div>

          {/* Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">ชั้นปี</label>
            <input
              type="number"
              value={formData.level}
              onChange={(e) => handleInputChange("level", e.target.value)}
              className={`block w-full px-3 py-3 border rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                              errors.level ? "border-red-300 focus:ring-red-500" : "border-brand-200"
              }`}
              placeholder="ชั้นปี เช่น 1, 2, 3, 4"
              min="1"
            />
            {errors.level && (
              <p className="mt-2 text-sm text-red-600 flex items-center space-x-1">
                <AlertCircle className="w-4 h-4" /> <span>{errors.level}</span>
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-semibold py-3 px-4 rounded-xl shadow-lg transition-all duration-200 disabled:opacity-70"
          >
            {isLoading ? "กำลังสมัคร..." : "สมัครสมาชิก"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            มีบัญชีแล้วใช่ไหม?{" "}
            <button
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(getSafeRedirect())}`)}
              className="text-green-600 hover:text-green-800 font-medium"
            >
              เข้าสู่ระบบ
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
